import React, { useCallback } from 'react';
import dayjs from 'dayjs';
import { getStoreIds, getTotalInvestment } from '../../../utils/tradeInvestment';

/** Búsqueda (nombre / descripción) con sugerencias para el listado de campañas. */
export function useCampaignSearch({ setRows, campaigns }) {
    const [suggestions, setSuggestions] = React.useState([]);
    const [searchValue, setSearchValue] = React.useState('');

    const handleSearch = (search) => {
        setSearchValue(search);
        if (!search || typeof search !== 'string') {
            setSuggestions([]);
            setRows(campaigns);
            return;
        }
        const filtered = campaigns.filter((campaign) => {
            return campaign.name?.toLowerCase().includes(search.toLowerCase()) || campaign.description?.toLowerCase().includes(search.toLowerCase());
        });
        setRows(filtered);
        setSuggestions(filtered.map((c) => c.name));
    };

    return { suggestions, searchValue, setSearchValue, handleSearch };
}

/** KPIs del listado de campañas. */
export function useCampaignStats(campaigns) {
    return React.useMemo(() => {
        const totalCampaigns = campaigns?.length || 0;
        const activeCampaigns = campaigns?.filter((c) => c.active && !dayjs(c.expirationDate).isBefore(dayjs(), 'day'))?.length || 0;
        const expiredCampaigns = campaigns?.filter((c) => dayjs(c.expirationDate).isBefore(dayjs(), 'day'))?.length || 0;
        const inactiveCampaigns = campaigns?.filter((c) => !c.active)?.length || 0;
        const totalInvestment = (campaigns || []).reduce((total, campaign) => total + getTotalInvestment(campaign), 0);
        const totalStores = (campaigns || []).reduce((total, campaign) => total + getStoreIds(campaign).length, 0);

        return { totalCampaigns, activeCampaigns, expiredCampaigns, inactiveCampaigns, totalInvestment, totalStores };
    }, [campaigns]);
}

/** Estado y envío del drawer de crear / editar campaña. */
export function useCampaignForm({ isOpen, setIsOpen, createCampaign, updateCampaign, stores, editMode, campaignData }) {
    const [name, setName] = React.useState('');
    const [campaignType, setCampaignType] = React.useState('pop_blando');
    const [loading, setLoading] = React.useState(false);
    const [isStoreModalOpen, setIsStoreModalOpen] = React.useState(false);
    const [selectedStores, setSelectedStores] = React.useState([]);
    const [content, setContent] = React.useState([{ material: '', channel: '' }]);
    const [startDate, setStartDate] = React.useState('');
    const [expirationDate, setExpirationDate] = React.useState('');
    const [price, setPrice] = React.useState(0);

    // Populate fields when editing
    React.useEffect(() => {
        if (editMode && campaignData) {
            setName(campaignData.name || '');
            setCampaignType(campaignData.campaignType || 'pop_blando');
            setContent(campaignData.content || [{ material: '', channel: '' }]);
            setSelectedStores(campaignData.stores?.map((s) => (typeof s === 'string' ? s : s._id)) || []);
            setStartDate(campaignData.startDate ? dayjs(campaignData.startDate).format('YYYY-MM-DD') : '');
            setExpirationDate(campaignData.expirationDate ? dayjs(campaignData.expirationDate).format('YYYY-MM-DD') : '');
            setPrice(campaignData.price ?? 0);
        } else {
            // Reset form when not editing
            setName('');
            setCampaignType('pop_blando');
            setContent([{ material: '', channel: '' }]);
            setSelectedStores([]);
            setStartDate('');
            setExpirationDate('');
            setPrice(0);
        }
    }, [editMode, campaignData, isOpen]);

    const filteredStores = React.useMemo(() => stores?.filter((store) => selectedStores.includes(store._id ?? '')), [stores, selectedStores]);

    const onCloseDrawer = useCallback(() => {
        setIsOpen(false);
    }, [setIsOpen]);

    const onSubmit = useCallback(async () => {
        try {
            setLoading(true);
            const payload = {
                name,
                campaignType,
                stores: selectedStores,
                content,
                startDate,
                expirationDate,
                price: Number(price) || 0
            };

            if (editMode && campaignData) {
                await updateCampaign(campaignData._id, payload);
            } else {
                await createCampaign(payload);
            }
            onCloseDrawer();
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [name, campaignType, selectedStores, content, startDate, expirationDate, price, createCampaign, updateCampaign, editMode, campaignData, onCloseDrawer]);

    const handleContentChange = useCallback((index, field, value) => {
        setContent((prev) => {
            const newContent = [...prev];
            newContent[index] = { ...newContent[index], [field]: value };
            return newContent;
        });
    }, []);

    const addContentField = useCallback(() => {
        setContent((prev) => [...prev, { material: '', channel: '' }]);
    }, []);

    const removeContentField = useCallback((index) => {
        setContent((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const estimatedInvestment = (Number(price) || 0) * selectedStores.length;

    return {
        name,
        setName,
        campaignType,
        setCampaignType,
        loading,
        isStoreModalOpen,
        setIsStoreModalOpen,
        selectedStores,
        setSelectedStores,
        content,
        startDate,
        setStartDate,
        expirationDate,
        setExpirationDate,
        price,
        setPrice,
        filteredStores,
        estimatedInvestment,
        today: dayjs(),
        onCloseDrawer,
        onSubmit,
        handleContentChange,
        addContentField,
        removeContentField
    };
}

/** Búsqueda y alta / baja de pisos en el modal de selección de la campaña. */
export function useStoreToggleList({ stores, selectedStores, setSelectedStores }) {
    const [search, setSearch] = React.useState('');
    const [filteredStores, setFilteredStores] = React.useState([]);

    const onSearch = (value) => {
        setSearch(value);
        setFilteredStores(stores?.filter((store) => store.name?.toLowerCase().includes(value.toLowerCase())));
    };

    const isSelected = (store) => selectedStores?.includes(store?._id ?? '');

    const toggleStore = (store) => {
        if (isSelected(store)) {
            setSelectedStores(selectedStores?.filter((id) => id !== store?._id));
        } else {
            setSelectedStores([...selectedStores, store?._id ?? '']);
        }
    };

    const displayedStores = search !== '' ? filteredStores : stores;

    return { search, onSearch, displayedStores, isSelected, toggleStore };
}
